<template>
    <div v-if="detail" class="mc-tech-stock-panel" data-testid="technician-stock-panel">
        <header class="mc-tech-stock-panel__header" :class="areaHeaderClass">
            <div class="mc-tech-stock-panel__title-row">
                <div>
                    <h3 class="mc-tech-stock-panel__name">{{ detail.name }}</h3>
                    <div class="mc-tech-stock-panel__status">
                        <span class="area-badge" :class="areaBadgeClass">{{ detail.area || 'West' }}</span>
                    </div>
                </div>
                <button class="mc-tech-stock-panel__close" @click="handleClose">
                    <i class="mc-fa icon-outline_close"></i>
                </button>
            </div>
        </header>

        <div class="mc-tech-stock-panel__body">
            <h5 class="mc-stock-title">Stock Teknisi</h5>

            <div v-if="isLoading" class="mc-stock-loading">
                <b-spinner small variant="primary" /> Memuat stok...
            </div>
            <div v-else-if="error" class="mc-stock-error">
                <i class="fas fa-exclamation-triangle"></i> {{ error }}
            </div>
            <div v-else-if="stockItems.length === 0" class="mc-stock-empty">
                <i class="fas fa-box-open"></i> Tidak ada stok untuk teknisi ini.
            </div>
            <b-table v-else :items="stockItems" :fields="tableFields" responsive striped hover small
                class="mc-stock-table" thead-class="mc-stock-table-header">
                <!-- Custom formatting for quantity column -->
                <template #cell(quantity)="data">
                    <span class="mc-stock-qty">{{ formatQuantity(data.value) }}</span>
                </template>
            </b-table>
        </div>

        <footer class="mc-tech-stock-panel__footer">
            <button type="button" class="mc-tech-stock-panel__action" @click="handleClose">
                Tutup
            </button>
        </footer>
    </div>
</template>

<script>
import { mapActions } from 'vuex';
import * as getStockTechnicianApi from '@/services/rest-api-sms/lib/erp/get-stock-technician'

export default {
    name: 'TechnicianStockPanel',
    props: {
        detail: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            stockItems: [],
            isLoading: false,
            error: null,
            tableFields: [
                { key: 'code', label: 'Kode', sortable: true, thClass: 'text-left', tdClass: 'text-left' },
                { key: 'name', label: 'Nama Produk', sortable: true, thClass: 'text-left', tdClass: 'text-left' },
                { key: 'quantity', label: 'Qty', sortable: true, thClass: 'text-right', tdClass: 'text-right' }
            ]
        };
    },
    computed: {
        areaHeaderClass() {
            const area = this.detail.area || 'West';
            return area === 'West' ? 'is-west' : 'is-east';
        },
        areaBadgeClass() {
            const area = this.detail.area || 'West';
            return area === 'West' ? 'badge-west' : 'badge-east';
        }
    },

    watch: {
        // 🔁 Setiap detail berubah (berarti teknisi berbeda), fetch ulang stok
        detail: {
            immediate: true,   // jalankan saat komponen dibuat (mounted tidak perlu lagi)
            handler(newVal, oldVal) {
                // Hanya fetch jika benar-benar berbeda (misal user_id berubah)
                if (newVal && (!oldVal || newVal.user_id !== oldVal.user_id)) {
                    this.fetchStock();
                }
            }
        }
    },

    methods: {
        ...mapActions('users', ['loadTechnicianStock']), // asumsikan ada action di store

        async fetchStock() {
            this.isLoading = true;
            this.error = null;
            try {
                const email = this.detail.email;
                if (!email) throw new Error('Email teknisi tidak ditemukan');

                const params = { email: encodeURIComponent(email) };
                const response = await getStockTechnicianApi.get(params);

                // Perbaiki pengecekan error
                if (!response.status) {
                    throw new Error(`Gagal mengambil data stok`);
                }

                if (!response.data) {
                    throw new Error(`Teknisi tidak memiliki stock`);
                }

                const data = response.data;
                // Asumsi data adalah array
                this.stockItems = data.map(item => ({
                    code: item.lot || '',
                    name: item.product || '',
                    quantity: item.quantity || 0
                }));
            } catch (err) {
                console.error(err);
                this.error = err.message || 'Gagal memuat stok teknisi';
            } finally {
                this.isLoading = false;
            }
        },

        formatQuantity(qty) {
            // Format seperti di gambar: angka dengan 2 desimal
            return parseFloat(qty).toFixed(2);
        },

        handleClose() {
            this.$emit('close');
        }
    }
};
</script>

<style scoped>
.mc-tech-stock-panel {
    font-family: inherit;
    width: 420px;
    max-width: 100%;
    background: #ffffff;
    display: flex;
    flex-direction: column;
    height: 100%;
    max-height: 100vh;
    overflow: hidden;
    position: absolute;
    top: 0;
    right: 0;
    box-shadow: -4px 0 20px rgba(0, 0, 0, 0.1);
    z-index: 1000;
}

.mc-tech-stock-panel__header {
    padding: 14px 16px 12px;
    border-bottom: 1px solid #eef2f7;
    background: #f8fafc;
    position: relative;
    min-height: 110px;
}

.mc-tech-stock-panel__header.is-west {
    background: #eff6ff;
}

.mc-tech-stock-panel__header.is-east {
    background: #f0fdf4;
}

.mc-tech-stock-panel__title-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 8px;
}

.mc-tech-stock-panel__name {
    margin: 0;
    font-size: 1rem;
    font-weight: 700;
    color: #0f172a;
}

.mc-tech-stock-panel__status {
    margin-top: 4px;
}

.area-badge {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 999px;
    font-size: 0.6875rem;
    font-weight: 600;
}

.badge-west {
    background: #bfdbfe;
    color: #1e3a8a;
}

.badge-east {
    background: #dcfce7;
    color: #15803d;
}

.mc-tech-stock-panel__close {
    font-size: 1.25rem;
    background: transparent;
    border: none;
    padding: 4px;
    border-radius: 6px;
    color: #64748b;
    cursor: pointer;
}

.mc-tech-stock-panel__close:hover {
    background: rgba(15, 23, 42, 0.08);
}

.mc-tech-stock-panel__body {
    padding: 16px;
    flex: 1;
    overflow-y: auto;
}

.mc-stock-title {
    font-size: 0.9rem;
    font-weight: 700;
    margin-bottom: 12px;
    color: #1e293b;
    border-left: 4px solid #2563eb;
    padding-left: 10px;
}

.mc-stock-loading,
.mc-stock-error,
.mc-stock-empty {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 24px;
    color: #64748b;
    text-align: center;
}

.mc-stock-error {
    color: #dc2626;
}

.mc-stock-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.mc-stock-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 12px;
    background: #f8fafc;
    border-radius: 8px;
    font-size: 0.8125rem;
    border: 1px solid #eef2f7;
}

.mc-stock-item__code {
    font-family: monospace;
    font-weight: 600;
    color: #475569;
    min-width: 70px;
}

.mc-stock-item__name {
    flex: 1;
    margin-left: 12px;
    color: #0f172a;
    font-weight: 500;
}

.mc-stock-item__qty {
    font-weight: 700;
    color: #2563eb;
    background: #eff6ff;
    padding: 2px 8px;
    border-radius: 20px;
    min-width: 50px;
    text-align: center;
}

.mc-tech-stock-panel__footer {
    display: flex;
    gap: 6px;
    padding: 12px 16px;
    background: #f8fafc;
    border-top: 1px solid #eef2f7;
}

.mc-tech-stock-panel__action {
    flex: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 8px 12px;
    background: #ffffff;
    border: 1px solid #e5e9f0;
    border-radius: 8px;
    font-size: 0.8125rem;
    font-weight: 600;
    color: #334155;
    cursor: pointer;
    transition: all 0.15s ease;
}

.mc-tech-stock-panel__action:hover {
    background: #f1f5f9;
    border-color: #cbd5e1;
}

.mc-stock-table {
    font-size: 0.8125rem;
}

.mc-stock-table>>>.table {
    margin-bottom: 0;
}

.mc-stock-table>>>th,
.mc-stock-table>>>td {
    vertical-align: middle;
    padding: 0.5rem 0.75rem;
}

.mc-stock-table-header th {
    background-color: #f1f5f9;
    color: #1e293b;
    font-weight: 600;
    border-bottom: 1px solid #e2e8f0;
}

.mc-stock-qty {
    font-weight: 700;
    color: #2563eb;
    background: #eff6ff;
    padding: 2px 8px;
    border-radius: 20px;
    display: inline-block;
    text-align: center;
    min-width: 60px;
}

/* pastikan responsive table tidak overflow */
.mc-tech-stock-panel__body {
    overflow-x: auto;
}
</style>
